import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Article, ArticleStatus } from "@/types/article-types";
import { supabase } from "@/integrations/supabase/client";
import { ArrowRight } from "lucide-react";

const LatestArticles = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLatestArticles = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('articles')
          .select(`
            *,
            author:author_id (
              id,
              first_name,
              last_name,
              profile_image_url
            )
          `)
          .eq('status', 'published')
          .order('publish_date', { ascending: false })
          .limit(3);

        if (error) throw error;

        const transformedData: Article[] = data.map(item => ({
          ...item,
          status: item.status as ArticleStatus
        }));

        setArticles(transformedData);
      } catch (err: any) {
        console.error("Error fetching articles:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestArticles();
  }, []);

  const truncateText = (text: string, maxLength: number): string => {
    if (!text) return "";
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  if (loading) {
    return (
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-corporate-primary-75 mb-4">
              Latest Articles
            </h2>
            <p className="text-corporate-neutral-75 text-lg max-w-2xl mx-auto">
              Loading the latest insights...
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-gray-100 animate-pulse rounded-xl h-64"></div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-corporate-primary-75 mb-4">
              Latest Articles
            </h2>
            <p className="text-red-500 mb-4">Unable to load articles</p>
          </div>
        </div>
      </section>
    );
  }

  if (articles.length === 0) {
    return null;
  }

  return (
    <section className="py-16 bg-white border-t border-corporate-neutral-25">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-corporate-primary-75 mb-4">
            Latest Articles
          </h2>
          <p className="text-corporate-neutral-75 text-lg max-w-2xl mx-auto">
            Insights, strategies, and updates from our experts
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {articles.map((article) => (
            <Link 
              key={article.id} 
              to={`/articles/${article.slug}`}
              className="border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow group"
            >
              <div className="h-48 overflow-hidden bg-gray-100">
                <img
                  src={article.header_image_url || "/lovable-uploads/bf9cdc94-db08-4840-bae6-869c2f0c41bb.png"}
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 className="font-bold text-xl mb-2 text-corporate-primary-100 group-hover:text-corporate-secondary-100 transition-colors">
                  {truncateText(article.title, 60)}
                </h3>
                <p className="text-corporate-neutral-75 mb-4 text-sm">
                  {truncateText(article.lead_text || "", 120)}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {article.author?.profile_image_url && (
                      <img
                        src={article.author.profile_image_url}
                        alt={`${article.author.first_name} ${article.author.last_name}`}
                        className="w-8 h-8 rounded-full mr-2 object-cover"
                      />
                    )}
                    <span className="text-sm text-corporate-neutral-50">
                      {article.author ? `${article.author.first_name} ${article.author.last_name}` : "METRADE Team"}
                    </span>
                  </div>
                  <span className="text-xs text-corporate-neutral-50">
                    {new Date(article.publish_date || article.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </motion.div>
        
        <div className="flex justify-center mt-10">
          <Link 
            to="/knowledge-hub/blog" 
            className="inline-flex items-center font-medium text-corporate-secondary-100 hover:text-corporate-secondary-75 transition-colors"
          >
            View all articles
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default LatestArticles;
